import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks/storeHooks/storehooks'
import { setSelectedOptions } from '@/redux-store/slices/dropdownOptionsSlice/dropdownOptionsSlice'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { GradientGridLayoutIcon } from '@/assets/svgIcons'
import { useAuth0 } from '@auth0/auth0-react'
import { addValues, formatCamelCaseLabel } from '@/lib/utils'
import { setWidgets } from '@/redux-store/slices/menuItemSlice/menuItemSlice'

interface WidgetItem {
  key: string
  name: string
  hasExpandedView: boolean
}

// Type for stored widget order
interface StoredWidgetOrder {
  widgetKeys: string[] // Array of widget keys in order
  timestamp: number
  version: string
}

const CheckboxDropdown: React.FC = () => {
  const dispatch = useAppDispatch()
  const { user } = useAuth0()
  const userName = user?.sub

  const currentVisibleItems = useAppSelector(
    state => state.dropdownOptionsClicked.selectedOptions,
  )

  const currentOverview = useAppSelector(state => state.menu.overView)
  const asd = useAppSelector(state => state.menu.widgets)
  const currentItems = asd?.[currentOverview] || []

  // Load widget order from localStorage
  const loadWidgetOrder = (
    userName: string,
    overview: string,
  ): string[] | null => {
    try {
      const widgetOrderKey = `${userName}_${overview}_widget_order`

      const storedOrder = localStorage.getItem(widgetOrderKey)

      if (storedOrder) {
        const parsedOrder: StoredWidgetOrder = JSON.parse(storedOrder)

        if (
          parsedOrder &&
          Array.isArray(parsedOrder.widgetKeys) &&
          typeof parsedOrder.timestamp === 'number' &&
          typeof parsedOrder.version === 'string'
        ) {
          return parsedOrder.widgetKeys
        }

        console.warn('Invalid widget order format in localStorage')
      }
    } catch (error) {
      console.error('Error loading widget order from localStorage:', error)
    }

    return null
  }

  const getPreferenceKey = (userName: string, overview: string) =>
    `${userName}_${overview}_preferences`

  // Enhanced function to apply widget order from localStorage
  const applyStoredWidgetOrder = (
    widgets: WidgetItem[],
    storedOrder: string[],
  ): WidgetItem[] => {
    if (!storedOrder || storedOrder.length === 0) {
      return widgets
    }

    const widgetMap = new Map<string, WidgetItem>()
    widgets.forEach(widget => {
      widgetMap.set(widget.key, widget)
    })

    const orderedWidgets: WidgetItem[] = []
    const usedKeys = new Set<string>()

    // Add widgets in the stored order
    storedOrder.forEach(key => {
      const widget = widgetMap.get(key)
      if (widget) {
        orderedWidgets.push(widget)
        usedKeys.add(key)
      }
    })

    // Add any remaining widgets that weren't in the stored order
    widgets.forEach(widget => {
      if (!usedKeys.has(widget.key)) {
        orderedWidgets.push(widget)
      }
    })

    return orderedWidgets
  }

  const hiddenByDefault: Record<string, string[]> = {
    Home: ['packetloss_metrics', 'latency_metrics'],
    'Technology & Network': [],
    'Service Delivery': [],
    'Billing & Usage': [],
  }
  useEffect(() => {
    let fallbackNeeded = false

    if (userName) {
      const storageKey = getPreferenceKey(userName, currentOverview)
      const storedOptions = localStorage.getItem(storageKey)
      if (storedOptions) {
        try {
          const parsedOptions = JSON.parse(storedOptions)

          if (Array.isArray(parsedOptions)) {
            const vals = addValues(parsedOptions, currentItems)
            dispatch(setSelectedOptions(vals))

            // Load and apply widget order
            const storedWidgetOrder = loadWidgetOrder(userName, currentOverview)

            if (storedWidgetOrder && currentItems.length > 0) {
              const orderedWidgets = applyStoredWidgetOrder(
                currentItems,
                storedWidgetOrder,
              )
              const currentWidgets = { ...asd }
              currentWidgets[currentOverview] = orderedWidgets
              dispatch(setWidgets(currentWidgets))
            }

            return // ✅ success, no fallback
          } else {
            console.warn('Invalid stored options format')
            fallbackNeeded = true
          }
        } catch (e) {
          console.error('Error parsing localStorage data', e)
          fallbackNeeded = true
        }
      } else {
        fallbackNeeded = true // nothing in localStorage
      }
    } else {
      fallbackNeeded = true // no username → straight to fallback
    }

    if (fallbackNeeded) {
      const updated = currentItems?.map(item => ({
        key: item.key,
        name: item.name,
        value: !(hiddenByDefault[currentOverview] || []).includes(item.key),
      }))
      dispatch(setSelectedOptions(updated))
    }
  }, [userName, currentOverview, currentItems.length])

  const handleCheckboxChange = (isChecked: boolean, key: string) => {
    const updatedOptions = currentVisibleItems.map(item =>
      item.name === key ? { ...item, value: isChecked } : item,
    )
    dispatch(setSelectedOptions(updatedOptions))
    storeCheckLocalStorage(userName, currentOverview, updatedOptions)
  }

  const storeCheckLocalStorage = (
    userName: string | undefined,
    overview: string,
    options: { key: string; name: string; value: boolean }[],
  ) => {
    if (!userName) return

    const key = getPreferenceKey(userName, overview)

    localStorage.setItem(key, JSON.stringify(options))
  }
  console.log(currentItems, currentOverview, 'currentItems')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className=' h-full' id='checkbox_dropdown'>
        <div className='p-2 gap-2 bg-tertiary flex justify-center items-center rounded-radius-md flex-shrink-0 cursor-pointer hover:scale-110 transition-transform'>
          <GradientGridLayoutIcon data-testid='grid-icon' />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-full mt-6 bg-primary min-w-[calc(100vh-668px)]'>
        <DropdownMenuItem onSelect={e => e.preventDefault()}>
          {currentVisibleItems?.map((option, index) => (
            // <div
            //   key={index}
            //   className='flex justify-start gap-2 items-center py-1 text-primary-foreground hover:bg-primary-hover px-2 rounded-radius-sm'
            // >
            //   <Checkbox
            //     checked={option.value}
            //     onCheckedChange={isChecked => {
            //       handleCheckboxChange(Boolean(isChecked), option.name)
            //     }}
            //     className='flex items-center border border-dashed border-primary-foreground h-6 w-8 justify-center'
            //   />
            //   <span className='mr-2'>{formatCamelCaseLabel(option.name)}</span>
            // </div>
            <div
              key={`${option.name}-${index}`}
              className='flex justify-start gap-2 items-center py-1 text-primary-foreground hover:bg-primary-hover px-2 rounded-radius-sm transition-all duration-200'
            >
              <Checkbox
                checked={option.value}
                onCheckedChange={isChecked => {
                  handleCheckboxChange(Boolean(isChecked), option.name)
                }}
                className='flex items-center border border-dashed border-primary-foreground h-6 w-8 justify-center'
              />
              <span className='mr-2'>{formatCamelCaseLabel(option.name)}</span>
            </div>
          ))}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default CheckboxDropdown
